
import React from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '@/components/admin/AdminLayout';
import { useAdminDashboard } from '@/hooks/useAdminDashboard';
import { useAuth } from '@/context/SupabaseAuthContext';
import { PERMISSIONS } from '@/utils/roleManagement';
import RoleBasedAccess from '@/components/auth/RoleBasedAccess';
import AdminDashboardAnalytics from '@/components/admin/dashboard/AdminDashboardAnalytics';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const role = profile?.role;
  const {
    user,
    referrals,
    authorizedUsers,
    isLoading,
    processingReferrals,
    activeTab,
    setActiveTab,
    handleLogout: logoutFromHook,
    handleApproveReferral,
    handleRejectReferral,
    handleAddAuthorizedUser,
    handleDeleteAuthorizedUser,
    exportReport
  } = useAdminDashboard();

  const handleLogout = () => {
    logoutFromHook();
    navigate('/login');
  };

  if (isLoading) {
    return (
      <AdminLayout
        isLoading={true}
        onLogout={handleLogout}
        userRole={role}
      >
        <div className="flex items-center justify-center h-full">
          <p>Loading dashboard data...</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout
      isLoading={false}
      onLogout={handleLogout}
      userRole={role}
    >
      <div className="space-y-8">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
          <p className="text-gray-600">Manage referrals and authorized users</p>
        </div>
        
        {/* Admin dashboard content */}
        <div className="mb-12">
          <AdminDashboardAnalytics />
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Referrals Section */}
          <RoleBasedAccess
            permission={PERMISSIONS.VIEW_CAMPAIGNS}
            userRole={role as any}
          >
            <div className="bg-white shadow overflow-hidden rounded-md">
              <div className="px-4 py-5 sm:px-6">
                <h3 className="text-lg font-medium text-gray-900">Referrals</h3>
                <p className="mt-1 max-w-2xl text-sm text-gray-500">
                  Manage and review user referrals.
                </p>
              </div>
              <div className="border-t border-gray-200">
                <ul className="divide-y divide-gray-200">
                  {referrals.length > 0 ? (
                    referrals.map((referral) => (
                      <li key={referral.id} className="px-4 py-4 sm:px-6">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium text-gray-900">{referral.name}</p>
                          <div className="ml-2 flex-shrink-0 flex">
                            {referral.status === 'pending' && (
                              <>
                                <button
                                  onClick={() => handleApproveReferral(referral.id)}
                                  className="relative inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                                  disabled={processingReferrals.includes(referral.id)}
                                >
                                  Approve
                                </button>
                                <button
                                  onClick={() => handleRejectReferral(referral.id)}
                                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                                  disabled={processingReferrals.includes(referral.id)}
                                >
                                  Reject
                                </button>
                              </>
                            )}
                            {processingReferrals.includes(referral.id) && (
                              <span className="text-sm text-gray-500">Processing...</span>
                            )}
                            {referral.status === 'approved' && (
                              <span className="text-sm text-green-500">Approved</span>
                            )}
                            {referral.status === 'rejected' && (
                              <span className="text-sm text-red-500">Rejected</span>
                            )}
                          </div>
                        </div>
                        <p className="mt-2 text-sm text-gray-500">
                          Email: {referral.email} | Status: {referral.status}
                        </p>
                      </li>
                    ))
                  ) : (
                    <li className="px-4 py-4 sm:px-6 text-center text-gray-500">
                      No referrals found
                    </li>
                  )}
                </ul>
              </div>
            </div>
          </RoleBasedAccess>
          
          {/* Authorized Users Section */}
          <RoleBasedAccess
            permission={PERMISSIONS.VIEW_USERS}
            userRole={role as any}
          >
            <div className="bg-white shadow overflow-hidden rounded-md">
              <div className="px-4 py-5 sm:px-6">
                <h3 className="text-lg font-medium text-gray-900">Authorized Users</h3>
                <p className="mt-1 max-w-2xl text-sm text-gray-500">
                  Manage users authorized to use the system.
                </p>
              </div>
              <div className="border-t border-gray-200">
                <ul className="divide-y divide-gray-200">
                  {authorizedUsers.length > 0 ? (
                    authorizedUsers.map((user) => (
                      <li key={user.id} className="px-4 py-4 sm:px-6">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium text-gray-900">{user.name}</p>
                          <div className="ml-2 flex-shrink-0 flex">
                            <button
                              onClick={() => handleDeleteAuthorizedUser(user.id)}
                              className="relative inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                        <p className="mt-2 text-sm text-gray-500">
                          Email: {user.email} | Account: {user.accountNumber}
                        </p>
                      </li>
                    ))
                  ) : (
                    <li className="px-4 py-4 sm:px-6 text-center text-gray-500">
                      No authorized users found
                    </li>
                  )}
                </ul>
              </div>
            </div>
          </RoleBasedAccess>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
